import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TegelModule } from '@scania/tegel-angular-17';
import { debounceTime, delay, of, switchMap, tap, timer } from 'rxjs';
import { TooltipConfig, TooltipService } from './tooltip.service';

@Component({
  selector: 'tooltip-outlet',
  imports: [TegelModule, CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (hasValidConfig() && tooltipConfig().referenceEl) {
      <tds-tooltip
        [text]="tooltipConfig().text || ''"
        [referenceEl]="tooltipConfig().referenceEl"
        [placement]="tooltipConfig().placement"
        [show]="showTooltip()"
        [offsetDistance]="tooltipConfig().offsetDistance"
      >
        @if (tooltipConfig().content) {
          <ng-container *ngTemplateOutlet="tooltipConfig().content"></ng-container>
        }
      </tds-tooltip>
    }
  `,
  styles: `
    tds-tooltip {
      z-index: 1001;
    }
  `
})
export class TooltipOutletComponent implements OnInit {
  private tooltipService = inject(TooltipService);
  private destroyRef = inject(DestroyRef);

  tooltipConfig = signal<TooltipConfig>({ referenceEl: null, text: '' });
  hasValidConfig = signal(false);
  showTooltip = signal(false);

  ngOnInit(): void {
    this.tooltipService.tooltipConfig$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(100),
        switchMap((config) => {
          // Immediately hide tooltip and reset state for any configuration update
          this.hasValidConfig.set(false);
          this.showTooltip.set(false);

          // If config is null, don't proceed with showing tooltip
          if (config === null) {
            return of(null); // Empty observable that completes immediately
          }

          // Otherwise, set the new config and schedule showing the tooltip
          this.tooltipConfig.set(config);

          return timer(1).pipe(
            tap(() => this.hasValidConfig.set(true)),
            delay(1),
            tap(() => this.showTooltip.set(true))
          );
        })
      )
      .subscribe();
  }
}
